import { stripVonq } from '@/utils/strip-vonq';

export type SelectOption = { value: string; label: string };

type RawOption = {
  key?: string | null;
  label?: string | null;
  parent?: string | null;
};

/**
 * For HIER fields the API (or static options) may return either:
 *   a) A hierarchical list mixing parent nodes (no `parent` field) and child
 *      nodes (`parent` pointing to a parent key). Per the VONQ spec only child
 *      keys should be submitted, so we keep only children and format their
 *      label as "Parent - Child" (e.g. "Kuwait - Hawally").
 *   b) A flat list where no option has a `parent` field (e.g. Monster location
 *      options). In this case we treat all options as selectable leaf nodes.
 */
export function flattenHierOptions(opts: RawOption[]): SelectOption[] {
  const hasChildren = opts.some(o => !!o.parent);
  if (!hasChildren) {
    return opts.map(o => ({
      value: o.key ?? '',
      label: stripVonq(String(o.label ?? o.key ?? '')),
    }));
  }
  const parentLabels: Record<string, string> = {};
  for (const o of opts) {
    if (!o.parent) parentLabels[o.key ?? ''] = String(o.label ?? o.key ?? '');
  }
  return opts
    .filter(o => !!o.parent)
    .map(o => ({
      value: o.key ?? '',
      label: stripVonq(
        o.parent && parentLabels[o.parent]
          ? `${parentLabels[o.parent]} - ${String(o.label ?? o.key ?? '')}`
          : String(o.label ?? o.key ?? ''),
      ),
    }));
}

/**
 * Maps raw VONQ options (static `field.options` or an API response's `options`)
 * to `{ value, label }` pairs for the select components. HIER fields are
 * flattened via `flattenHierOptions`; all others map key/label directly.
 */
export function mapOptions(
  opts: RawOption[] | null | undefined,
  isHier: boolean,
): SelectOption[] {
  if (isHier) return flattenHierOptions(opts ?? []);
  return (opts ?? []).map(opt => ({
    value: opt.key ?? '',
    label: stripVonq(String(opt.label ?? opt.key ?? '')),
  }));
}
